/**
 * Command Palette for the calendar arche.
 *
 * Opens via Cmd+K / Ctrl+K or `/`. Provides fuzzy-searchable commands
 * for navigation, view switching, and event creation.
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Plus,
  Calendar,
  ArrowLeft,
  ArrowRight,
  Eye,
  Settings,
  Upload,
  ChevronRight,
  Layers,
  FileText,
} from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import type { CalendarEventTemplateDetail } from '@/lib/api';
import type { CalendarView, CalendarSet } from '../types';

interface Command {
  id: string;
  label: string;
  keywords: string[];
  icon: React.ReactNode;
  action: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onNewEvent: () => void;
  onToday: () => void;
  onPrev: () => void;
  onNext: () => void;
  onViewChange: (view: CalendarView) => void;
  onSettings: () => void;
  onImport: () => void;
  onGoToDate: (date: Date) => void;
  calendarSets?: CalendarSet[];
  onActivateSet?: (id: string) => void;
  templates?: CalendarEventTemplateDetail[];
  onNewEventFromTemplate?: (template: CalendarEventTemplateDetail) => void;
  onManageTemplates?: () => void;
}

function fuzzyMatch(query: string, text: string): boolean {
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  if (t.includes(q)) return true;
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length;
}

function tryParseDate(input: string): Date | null {
  const trimmed = input.trim();
  // Try native Date parsing for formats like "2026-03-15", "March 15", "Mar 15 2026"
  const parsed = new Date(trimmed);
  if (!isNaN(parsed.getTime())) return parsed;

  // Try "MM/DD" or "MM/DD/YYYY" format
  const slashParts = trimmed.split('/');
  if (slashParts.length >= 2) {
    const month = parseInt(slashParts[0], 10) - 1;
    const day = parseInt(slashParts[1], 10);
    const year = slashParts[2] ? parseInt(slashParts[2], 10) : new Date().getFullYear();
    const d = new Date(year, month, day);
    if (!isNaN(d.getTime())) return d;
  }

  return null;
}

const iconClass = 'h-4 w-4 text-muted-foreground';

export function CommandPalette({
  open,
  onClose,
  onNewEvent,
  onToday,
  onPrev,
  onNext,
  onViewChange,
  onSettings,
  onImport,
  onGoToDate,
  calendarSets,
  onActivateSet,
  templates,
  onNewEventFromTemplate,
  onManageTemplates,
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const commands: Command[] = useMemo(
    () => [
      {
        id: 'new-event',
        label: 'Create new event',
        keywords: ['new', 'create', 'add', 'event'],
        icon: <Plus className={iconClass} />,
        action: onNewEvent,
      },
      {
        id: 'today',
        label: 'Go to today',
        keywords: ['today', 'now', 'current'],
        icon: <Calendar className={iconClass} />,
        action: onToday,
      },
      {
        id: 'prev',
        label: 'Go to previous period',
        keywords: ['previous', 'back', 'prev'],
        icon: <ArrowLeft className={iconClass} />,
        action: onPrev,
      },
      {
        id: 'next',
        label: 'Go to next period',
        keywords: ['next', 'forward'],
        icon: <ArrowRight className={iconClass} />,
        action: onNext,
      },
      {
        id: 'month-view',
        label: 'Switch to month view',
        keywords: ['month', 'view'],
        icon: <Eye className={iconClass} />,
        action: () => onViewChange('month'),
      },
      {
        id: 'week-view',
        label: 'Switch to week view',
        keywords: ['week', 'view'],
        icon: <Eye className={iconClass} />,
        action: () => onViewChange('week'),
      },
      {
        id: 'day-view',
        label: 'Switch to day view',
        keywords: ['day', 'view'],
        icon: <Eye className={iconClass} />,
        action: () => onViewChange('day'),
      },
      {
        id: 'agenda-view',
        label: 'Switch to agenda view',
        keywords: ['agenda', 'list', 'view'],
        icon: <Eye className={iconClass} />,
        action: () => onViewChange('agenda'),
      },
      {
        id: 'settings',
        label: 'Open settings',
        keywords: ['settings', 'preferences', 'options', 'config'],
        icon: <Settings className={iconClass} />,
        action: onSettings,
      },
      {
        id: 'import',
        label: 'Import calendar',
        keywords: ['import', 'upload', 'ical', 'ics'],
        icon: <Upload className={iconClass} />,
        action: onImport,
      },
      // Calendar set commands
      ...(calendarSets || []).map((set) => ({
        id: `set-${set.id}`,
        label: `Switch to set: ${set.name}`,
        keywords: ['set', 'switch', 'calendar', set.name.toLowerCase()],
        icon: <Layers className={iconClass} />,
        action: () => onActivateSet?.(set.id),
      })),
      // Template commands
      ...(templates || []).map((t) => ({
        id: `template-${t.id}`,
        label: `New event from template: ${t.name}`,
        keywords: ['template', 'new', 'event', t.name.toLowerCase()],
        icon: <FileText className={iconClass} />,
        action: () => onNewEventFromTemplate?.(t),
      })),
      // Manage templates command
      ...(onManageTemplates
        ? [
            {
              id: 'manage-templates',
              label: 'Manage templates',
              keywords: ['template', 'manage', 'edit', 'blueprint'],
              icon: <FileText className={iconClass} />,
              action: onManageTemplates,
            },
          ]
        : []),
    ],
    [
      onNewEvent,
      onToday,
      onPrev,
      onNext,
      onViewChange,
      onSettings,
      onImport,
      calendarSets,
      onActivateSet,
      templates,
      onNewEventFromTemplate,
      onManageTemplates,
    ],
  );

  const filteredCommands = useMemo(() => {
    if (!query.trim()) return commands;
    return commands.filter(
      (cmd) => fuzzyMatch(query, cmd.label) || cmd.keywords.some((kw) => fuzzyMatch(query, kw)),
    );
  }, [query, commands]);

  // Check if query looks like a date
  const dateCommand = useMemo((): Command | null => {
    if (!query.trim()) return null;
    const parsed = tryParseDate(query);
    if (!parsed) return null;
    return {
      id: 'go-to-date',
      label: `Go to ${parsed.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}`,
      keywords: [],
      icon: <ChevronRight className={iconClass} />,
      action: () => onGoToDate(parsed),
    };
  }, [query, onGoToDate]);

  const allResults = useMemo(() => {
    const results = [...filteredCommands];
    if (dateCommand) results.unshift(dateCommand);
    return results;
  }, [filteredCommands, dateCommand]);

  // Reset state when opening/closing
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [open]);

  // Keep selected index in bounds
  useEffect(() => {
    if (selectedIndex >= allResults.length) {
      setSelectedIndex(Math.max(0, allResults.length - 1));
    }
  }, [allResults.length, selectedIndex]);

  // Scroll selected item into view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const selected = list.children[selectedIndex] as HTMLElement | undefined;
    selected?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const executeCommand = useCallback(
    (cmd: Command) => {
      onClose();
      // Defer action so dialog closes first
      requestAnimationFrame(() => cmd.action());
    },
    [onClose],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((i) => (i + 1) % allResults.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((i) => (i - 1 + allResults.length) % allResults.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (allResults[selectedIndex]) {
            executeCommand(allResults[selectedIndex]);
          }
          break;
      }
    },
    [allResults, selectedIndex, executeCommand],
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="p-0 gap-0 max-w-md" hideClose onKeyDown={handleKeyDown}>
        <div className="flex items-center border-b px-3">
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            placeholder="Type a command or date..."
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-11"
            autoFocus
          />
        </div>
        <div ref={listRef} className="max-h-64 overflow-y-auto py-1">
          {allResults.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">No matching commands</p>
          )}
          {allResults.map((cmd, i) => (
            <button
              key={cmd.id}
              onClick={() => executeCommand(cmd)}
              onMouseEnter={() => setSelectedIndex(i)}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors ${
                i === selectedIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
              }`}
            >
              {cmd.icon}
              <span>{cmd.label}</span>
            </button>
          ))}
        </div>
        <div className="border-t px-3 py-1.5 flex items-center gap-3 text-xs text-muted-foreground">
          <span>
            <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">↑↓</kbd> navigate
          </span>
          <span>
            <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">↵</kbd> select
          </span>
          <span>
            <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">esc</kbd> close
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
